import { CardElement, Elements } from '@stripe/react-stripe-js'
import type { Stripe, StripeCardElementOptions } from '@stripe/stripe-js'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Label } from '#/shared/ui/label'

import type { UseAddCardArgs } from '../model/add-card-shared'
import { useAddCard } from '../model/use-add-card'
import type { UseAddCardResult } from '../model/use-add-card'
import { useCardBootstrap } from '../model/use-card-bootstrap'

import { AddCardAuthnetPanel } from './add-card-authnet-panel'
import {
  AddCardBody,
  AddCardFooter,
  BootstrapLoading,
  GatewayUnavailable,
  HttpsRequiredNotice,
} from './add-card-form-fields'

function isHttpsPage(): boolean {
  return window.location.protocol === 'https:'
}

const CARD_ELEMENT_OPTIONS: StripeCardElementOptions = {
  style: {
    base: {
      fontSize: '14px',
      color: '#21232C',
      fontFamily: 'inherit',
      '::placeholder': { color: '#A4A4A4' },
    },
    invalid: { color: '#D32D41' },
  },
}

interface AddCardPanelProps {
  customerId: string | undefined
  customerEmail: string | undefined
  onBack: () => void
}

function StripeCardField({
  onCardChange,
  cardError,
}: Pick<UseAddCardResult, 'onCardChange' | 'cardError'>) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-0.5">
      <Label size="sm">{t('addCard.cardDetails')}</Label>
      <div
        className={cn(
          'flex h-10 items-center rounded-md border bg-white px-2.5 transition-[border-color] duration-200 focus-within:focus-ring',
          cardError ? 'border-red-500' : 'border-gray-200',
        )}
      >
        <div className="w-full">
          <CardElement options={CARD_ELEMENT_OPTIONS} onChange={onCardChange} />
        </div>
      </div>
      {cardError && <p className="text-xs text-red-500">{cardError}</p>}
    </div>
  )
}

function StripeCardPanel({
  args,
  onBack,
}: {
  args: UseAddCardArgs
  onBack: () => void
}) {
  const add = useAddCard(args)
  return (
    <>
      <AddCardBody
        fields={add}
        cardField={
          <StripeCardField
            onCardChange={add.onCardChange}
            cardError={add.cardError}
          />
        }
      />
      <AddCardFooter
        onCancel={onBack}
        onPrimary={add.submit}
        canSubmit={add.canSubmit}
        isSubmitting={add.isSubmitting}
      />
    </>
  )
}

function StripePanel({
  stripe,
  args,
  onBack,
}: {
  stripe: Promise<Stripe | null>
  args: UseAddCardArgs
  onBack: () => void
}) {
  return (
    <Elements stripe={stripe}>
      <StripeCardPanel args={args} onBack={onBack} />
    </Elements>
  )
}

export function AddCardPanel({
  customerId,
  customerEmail,
  onBack,
}: AddCardPanelProps) {
  const { stripe, authnet, isLoading, isUnavailable } = useCardBootstrap()
  const args: UseAddCardArgs = { customerId, customerEmail, onSaved: onBack }

  if (isLoading) return <BootstrapLoading />
  if (stripe) return <StripePanel stripe={stripe} args={args} onBack={onBack} />
  if (authnet) {
    if (!isHttpsPage()) return <HttpsRequiredNotice />
    return <AddCardAuthnetPanel args={args} authnet={authnet} onBack={onBack} />
  }
  if (isUnavailable) return <GatewayUnavailable />
  return <BootstrapLoading />
}
